import { z } from "zod";

/**
 * How an ad is made: the owner on camera, a screen recording, a clip from existing video, one image,
 * photos people swipe (a carousel), or a post the business already published on its Facebook Page.
 */
export const AdFormatSchema = z.enum(["camera", "screen", "clip", "image", "carousel", "post"]);
export type AdFormat = z.infer<typeof AdFormatSchema>;
/** The formats an ad carries one video or photo in. */
export const SINGLE_FORMATS: readonly AdFormat[] = ["camera", "screen", "clip", "image"];

/** One card of photos people swipe: its id (the photo is kept under it), headline and line under it. */
export const AdCardSchema = z.object({
  id: z.string().regex(/^c[0-9a-z]{1,12}$/),
  headline: z.string().trim().max(60).default(""),
  description: z.string().trim().max(80).default(""),
});
export type AdCard = z.infer<typeof AdCardSchema>;
export const CARDS_MIN = 2;
export const CARDS_MAX = 10;

/** Other words Meta can try on the same ad (Meta's multiple text options). */
export const AdAltSchema = z.object({
  primary: z.string().trim().min(1).max(500).describe("Another ad text, about 125 characters, saying the same thing another way"),
  headline: z.string().trim().min(1).max(60).describe("Another headline, 27 to 40 characters"),
});
export const ALTS_MAX = 4;

/** A post the business already published on its Facebook Page, as Meta lists it. */
export const AdPostSchema = z.object({
  id: z.string().regex(/^\d+_\d+$/),
  kind: z.string().max(40),
  text: z.string().max(5000).default(""),
  at: z.string().max(40),
  reactions: z.number().int().nonnegative().default(0),
  comments: z.number().int().nonnegative().default(0),
  picture: z.string().url().max(2000).nullable().default(null),
  permalink: z.string().url().max(2000).nullable().default(null),
});
export type AdPost = z.infer<typeof AdPostSchema>;

/** The buttons Meta allows on these ads, as Zernio names them, with the words on the button. */
export const AD_BUTTONS = {
  GET_QUOTE: "Get quote",
  LEARN_MORE: "Learn more",
  SIGN_UP: "Sign up",
  BOOK_NOW: "Book now",
  CONTACT_US: "Contact us",
  APPLY_NOW: "Apply now",
  GET_OFFER: "Get offer",
  SUBSCRIBE: "Subscribe",
  SHOP_NOW: "Shop now",
  WATCH_MORE: "Watch more",
} as const;
export const AdButtonSchema = z.enum(Object.keys(AD_BUTTONS) as [keyof typeof AD_BUTTONS, ...(keyof typeof AD_BUTTONS)[]]);

/**
 * The words of one ad card, as the agent drafts them and the owner edits them. Only an ad of the owner on
 * camera may carry a script, and only when asked for one.
 */
export const AdWordsSchema = z.object({
  name: z.string().trim().min(1).max(80).describe("Short and plain, as the owner would call this idea"),
  format: AdFormatSchema,
  chips: z.array(z.string().trim().min(1).max(30)).max(4).default([]).describe("Two or three words each: the angle, the format"),
  says: z.string().trim().min(1).max(2000).optional().describe("A script to read, one line per beat: only for an ad of the owner on camera, and only when the card asks for one"),
  primary: z.string().trim().min(1).max(500).describe("The ad text above the video, about 125 characters"),
  headline: z.string().trim().min(1).max(60).describe("27 to 40 characters"),
  cta: AdButtonSchema.optional().describe("The ad's button, one Meta allows for the goal's result"),
  cards: z.array(AdCardSchema).max(CARDS_MAX).optional().describe("Leave out: the cards of photos people swipe"),
  alts: z.array(AdAltSchema).max(ALTS_MAX).optional().describe("Leave out: other words Meta can try"),
  ai: z.boolean().optional().describe("Leave out: the owner says the ad was made or edited with AI"),
  post: AdPostSchema.optional().describe("Leave out: the published post this ad promotes"),
});
export type AdWords = z.infer<typeof AdWordsSchema>;

export const AdDraftSchema = AdWordsSchema.omit({ cards: true, alts: true, ai: true, post: true }).extend({
  idea_key: z.string().trim().min(1).max(60).describe("The key of the card you were given"),
  cards: z
    .array(z.object({ headline: z.string().trim().min(1).max(40).describe("Up to 40 characters"), description: z.string().trim().max(60).default("").describe("One short line, up to 60 characters") }))
    .max(CARDS_MAX)
    .optional()
    .describe("For photos people swipe: a headline and one line for each card, in the card order you were given"),
  alts: z.array(AdAltSchema).max(ALTS_MAX).optional().describe("Only when the card asks for other words: that many more versions of the ad text and headline"),
});
export type AdDraft = z.infer<typeof AdDraftSchema>;

/** One cold email step, as the agent drafts it and the owner edits it. */
export const EmailStepSchema = z.object({
  step: z.number().int().min(1).max(5),
  subject: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(4000).describe("Use {first name} and {first line} where each person's own words go"),
  when: z.string().trim().min(1).max(80).describe('When it goes, e.g. "Day 1", "3 days later"'),
});
export type EmailStep = z.infer<typeof EmailStepSchema>;
